import PropTypes from 'prop-types';
import React from 'react';

import {
  Collaborator,
  COLLABORATOR_SHAPE,
  Ownable,
  OWNER_INFO_SHAPE,
  Permissions,
  PERMISSIONS_SHAPE,
} from 'auth/PermissionConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ENTITY_SHAPE} from 'constants/UserConstants';
import {OwnableInfo, UpdateOwnableParams} from 'create-components/OwnableConstants';
import OwnableSettingsModal from 'create-components/OwnableSettingsModal';
import ModalAnchor from 'ui-library/ModalAnchor';
import SettingsPage from 'ui-library/SettingsPage';
import {capwords, typeToName} from 'util/NameUtil';

type OwnableWithPermissions = Omit<Ownable, 'prefix' | 'collaborators'> & {permissions: Permissions};

interface OwnablePageProps {
  ownableType:
    | ModelTypeConstants.ENZYME_LIST
    | ModelTypeConstants.FEATURE_LIBRARY
    | ModelTypeConstants.LADDER;
  ownable: OwnableWithPermissions;
  collaborators: Array<Collaborator>;
  onExport?: () => void;
  onRequestSubmit: (updates: Omit<UpdateOwnableParams, 'owner'> & {owner: string}) => void;
  onRequestDelete: () => void;
}

interface OwnablePageState {
  isSettingsModalShown: boolean;
}

class OwnablePage extends React.Component<OwnablePageProps, OwnablePageState> {
  static propTypes = {
    ownableType: PropTypes.string.isRequired,
    ownable: PropTypes.shape({
      name: PropTypes.string.isRequired,
      owner: ENTITY_SHAPE.isRequired,
      description: PropTypes.string.isRequired,
      ownerInfo: OWNER_INFO_SHAPE.isRequired,
      permissions: PERMISSIONS_SHAPE,
    }).isRequired,
    collaborators: PropTypes.arrayOf(COLLABORATOR_SHAPE.isRequired),
    // If specified, an export icon will be shown that calls this callback.
    onExport: PropTypes.func,
    onRequestSubmit: PropTypes.func.isRequired,
    onRequestDelete: PropTypes.func.isRequired,
  };

  override state: OwnablePageState = {
    isSettingsModalShown: false,
  };

  _handleChangeIsSettingsModalShown = (value: boolean): void => {
    this.setState({isSettingsModalShown: value});
  };

  _getExistingOwnable = (): OwnableInfo => {
    const {name, description, ownerInfo, owner} = this.props.ownable;
    return {
      name,
      description,
      ownerInfo,
      collaborators: this.props.collaborators,
      owner,
    };
  };

  _handleSubmit = (updates: UpdateOwnableParams): void => {
    this.props.onRequestSubmit({
      ...updates,
      owner: updates.owner.handle,
    });
  };

  override render(): JSX.Element {
    const friendlyType = capwords(typeToName(this.props.ownableType));
    return (
      <SettingsPage
        title={this.props.ownable.name}
        type={friendlyType}
        isSettingsShown={this.state.isSettingsModalShown}
        onChangeIsSettingsShown={this._handleChangeIsSettingsModalShown}
        onExport={this.props.onExport}
      >
        <div className="u-tiny-spacing-top">{this.props.ownable.description}</div>
        {this.state.isSettingsModalShown && (
          <ModalAnchor open={true}>
            <OwnableSettingsModal
              onRequestClose={() => {
                this._handleChangeIsSettingsModalShown(false);
              }}
              ownableType={this.props.ownableType}
              existingOwnable={this._getExistingOwnable()}
              // @ts-expect-error _handleSubmit ultimately calls an async function, but OwnablePage._handleSubmit and
              // the parent OwnablesManageView.handleSaveSubmit both drop the promise on the floor
              doSubmit={this._handleSubmit}
              doDelete={this.props.onRequestDelete}
              showConfirmDelete={true}
              navigateOnSuccess={false}
              isReadOnly={!this.props.ownable.permissions.admin}
            />
          </ModalAnchor>
        )}
      </SettingsPage>
    );
  }
}

export default OwnablePage;
